/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Chaitanya
*/

import React, { useMemo } from 'react';
import { View } from 'react-native';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import { hasNoAccess } from '@global/helpers';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import * as Layout from '@components/screen/Layout';
import { AUTOMATE_CREDIT_LIMIT } from '@/constants';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetCreditLimitListQuery,
  useGetCreditLimitCountQuery,
  useGetCreditLimitInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  name: { head: 'Name', body: 'name' },
  status: { head: 'Status', body: 'is_active', type: 'status_active' },
  market_type: { head: 'Market type', body: 'market_type.code' },
  payment_terms: { head: 'Payment Terms', body: 'payment_terms.name' },
  automate_credit_limit: {
    head: 'Automate Credit Limit',
    body: 'automate_credit_limit',
  },
};

// Search-only: the search box writes `name`; unwrap it into a $regex query.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  return filter;
};

// Display row (RTK cache frozen → new object): map the automate-credit-limit label.
const mapRow = (res) => {
  const next = { ...res, id: res._id };
  if (res.automate_credit_limit) {
    const cl = AUTOMATE_CREDIT_LIMIT.find(
      (v) => v.value == res.automate_credit_limit,
    );
    if (cl) {
      next.automate_credit_limit = cl.label;
    }
  }
  return next;
};

const CreditLimitScreen = (props) => {
  // ── derived ──
  const showAddButton = !hasNoAccess(['superadmin', 'general']);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetCreditLimitListQuery,
    countHook: useGetCreditLimitCountQuery,
    infiniteHook: useGetCreditLimitInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = Array.isArray(list.data) ? list.data : [];
    return raw.map(mapRow);
  }, [list.data]);

  // ── handlers ──
  const goToDetail = ({ data }) => {
    openChild('CreditLimitDetailScreen', {
      id: data._id,
      titleScreen: data.name,
    });
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Customer Credit Limit">
            {showAddButton ? (
              <View style={{ flexDirection: 'row' }}>
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() => openChild('CreditLimitFormScreen')}
                />
              </View>
            ) : (
              false
            )}
          </Layout.Title>

          <Table
            filterName={scope}
            searchKey="name"
            searchPlaceholder="Search by name"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            hasCallback={true}
            showFilter={false}
            onClickRow={(val) => goToDetail(val)}
            searchValue={searchValue}
          />
        </>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

export default CreditLimitScreen;
